import { apiGet, apiSend } from './client';
import type { Environment } from '../constants';
import type {
  IBillingProfile,
  IInvoice,
  IInvoiceInput,
  IInvoicePreview,
  ISendInvoiceInput,
  ITenantBilling,
  ITenantBillingInput,
} from '../types';

const tenantPath = (tenant: string) => `/tenants/${encodeURIComponent(tenant)}`;
const invoicePath = (tenant: string, id: string) =>
  `${tenantPath(tenant)}/invoices/${encodeURIComponent(id)}`;

export const getBillingProfile = (environment: Environment): Promise<{ data: IBillingProfile }> =>
  apiGet(environment, '/billing/profile');

export const saveBillingProfile = (
  environment: Environment,
  profile: Omit<IBillingProfile, 'updated_at' | 'updated_by'>,
): Promise<{ data: IBillingProfile }> => apiSend(environment, '/billing/profile', 'PUT', profile);

export const getAllInvoices = (environment: Environment): Promise<{ data: IInvoice[] }> =>
  apiGet(environment, '/invoices');

export const getTenantBilling = (
  environment: Environment,
  tenant: string,
): Promise<{ data: ITenantBilling | null }> => apiGet(environment, `${tenantPath(tenant)}/billing`);

export const saveTenantBilling = (
  environment: Environment,
  tenant: string,
  input: ITenantBillingInput,
): Promise<{ data: ITenantBilling }> =>
  apiSend(environment, `${tenantPath(tenant)}/billing`, 'PUT', input);

export const getInvoices = (
  environment: Environment,
  tenant: string,
): Promise<{ data: IInvoice[] }> => apiGet(environment, `${tenantPath(tenant)}/invoices`);

export const previewInvoice = (
  environment: Environment,
  tenant: string,
  params: { period_start?: string; period_end?: string; issued_on?: string } = {},
): Promise<{ data: IInvoicePreview }> => {
  const query = new URLSearchParams(
    Object.entries(params).filter((entry): entry is [string, string] => Boolean(entry[1])),
  ).toString();
  return apiGet(environment, `${tenantPath(tenant)}/invoices/preview${query ? `?${query}` : ''}`);
};

export const createInvoice = (
  environment: Environment,
  tenant: string,
  input: IInvoiceInput,
): Promise<{ data: IInvoice }> =>
  apiSend(environment, `${tenantPath(tenant)}/invoices`, 'POST', input);

export const getInvoice = (
  environment: Environment,
  tenant: string,
  id: string,
): Promise<{ data: IInvoice }> => apiGet(environment, invoicePath(tenant, id));

export const updateInvoice = (
  environment: Environment,
  tenant: string,
  id: string,
  input: Partial<IInvoiceInput>,
): Promise<{ data: IInvoice }> => apiSend(environment, invoicePath(tenant, id), 'PUT', input);

export const issueInvoice = (
  environment: Environment,
  tenant: string,
  id: string,
): Promise<{ data: IInvoice }> => apiSend(environment, `${invoicePath(tenant, id)}/issue`, 'POST');

export const getInvoicePdfUrl = (
  environment: Environment,
  tenant: string,
  id: string,
  download: boolean,
): Promise<{ data: { url: string; filename: string; expires_in: number } }> =>
  apiGet(environment, `${invoicePath(tenant, id)}/pdf${download ? '?download=1' : ''}`);

export const sendInvoice = (
  environment: Environment,
  tenant: string,
  id: string,
  input: ISendInvoiceInput,
): Promise<{ data: IInvoice & { message_id: string | null } }> =>
  apiSend(environment, `${invoicePath(tenant, id)}/send`, 'POST', input);

export const markInvoicePaid = (
  environment: Environment,
  tenant: string,
  id: string,
  paidOn: string,
): Promise<{ data: IInvoice }> =>
  apiSend(environment, `${invoicePath(tenant, id)}/paid`, 'POST', { paid_on: paidOn });

export const voidInvoice = (
  environment: Environment,
  tenant: string,
  id: string,
  reason: string | null,
): Promise<{ data: IInvoice }> =>
  apiSend(environment, `${invoicePath(tenant, id)}/void`, 'POST', { reason });
